// Live-league hero HS4 — data hook (read-only, honest-empty).
//
// Players come from live_league_hero_players() (security-definer,
// itself gate-gated to live_sport_leagues() — a dormant league
// returns nothing even if a stray row existed). Fixtures reuse the
// existing useTodayFixtures view, filtered to this league by name.
// Soft-fails to empty arrays; never throws, never fabricates a
// squad or a scoreline.

import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useTodayFixtures } from "../super-acca/useTodayFixtures.ts";
import type { HeroPlayer, HeroFixture } from "./components/LiveLeagueHero.tsx";

interface RpcPlayer {
  player_id: number;
  player_name: string;
  team_name: string | null;
}

export interface UseLiveLeagueHeroState {
  readonly players: readonly HeroPlayer[];
  readonly fixtures: readonly HeroFixture[];
  readonly loading: boolean;
}

export function useLiveLeagueHero(
  leagueCode: string,
  leagueName: string,
): UseLiveLeagueHeroState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const [players, setPlayers] = useState<HeroPlayer[]>([]);
  const [loadingP, setLoadingP] = useState(true);
  const { fixtures: today, loading: loadingF } = useTodayFixtures();

  const loadPlayers = useCallback(async (): Promise<void> => {
    setLoadingP(true);
    try {
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: RpcPlayer[] | null; error: unknown }>)(
        "live_league_hero_players",
        { p_league_code: leagueCode, p_max: 6 },
      );
      if (error || !Array.isArray(data)) {
        setPlayers([]);
      } else {
        setPlayers(
          data
            .filter(
              (r) =>
                r &&
                Number.isInteger(r.player_id) &&
                r.player_id > 0 &&
                typeof r.player_name === "string" &&
                r.player_name.length > 0,
            )
            .map((r) => ({
              playerId: r.player_id,
              playerName: r.player_name,
              teamName: r.team_name ?? null,
            })),
        );
      }
    } catch {
      setPlayers([]);
    } finally {
      setLoadingP(false);
    }
  }, [supabase, leagueCode]);

  useEffect(() => {
    void loadPlayers();
  }, [loadPlayers]);

  const fixtures = useMemo<HeroFixture[]>(
    () =>
      today
        .filter((f) => f.league === leagueName)
        .map((f) => ({
          id: f.id,
          home: f.home,
          away: f.away,
          homeBadge: f.homeBadge,
          awayBadge: f.awayBadge,
          kickoff: f.kickoff,
          status: f.status,
          score: f.liveScore ?? f.finalScore ?? null,
        })),
    [today, leagueName],
  );

  return useMemo(
    () => ({ players, fixtures, loading: loadingP || loadingF }),
    [players, fixtures, loadingP, loadingF],
  );
}
